'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { keepPreviousData, tsClientOptions, useInfiniteQuery } from '@zg-rentals/react-query5';
import type { InfiniteData } from '@zg-rentals/react-query5';
import { SatelliteWebRenterConversationHandler_getLatestConversations } from '@zg-rentals/ts-satellite-client';
import type {
  SatelliteConversationFolderTypeV2,
  SatelliteWebRenterConversationGetLatestConversationsDto,
  SatelliteWebRenterConversationGetLatestConversationsRequestDto,
  SatelliteWebRenterConversationGetLatestConversationsResponseDto,
} from '@zg-rentals/ts-satellite-client';
import { SatelliteWebRenterConversationHandler_getLatestConversations_queryOptions } from '@zg-rentals/ts-satellite-client-react-query';

import { useInboxConfig } from '../InboxProvider';

export const CONVERSATION_LIST_QUERY_TAG = 'conversation-list';

type UseInfiniteConversationListOptions = {
  folder: SatelliteConversationFolderTypeV2;
  daysBack: number | null;
  isWebSocketEnabledAndReady: boolean;
  pollIntervalMs: number;
};

type UseInfiniteConversationListResult = {
  conversations: Array<SatelliteWebRenterConversationGetLatestConversationsDto>;
  hasNextPage: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isRefetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  fetchNextPage: () => Promise<void>;
};

function dedupeByConversationId(
  list: Array<SatelliteWebRenterConversationGetLatestConversationsDto>,
): Array<SatelliteWebRenterConversationGetLatestConversationsDto> {
  const seen = new Set<string>();
  const result: Array<SatelliteWebRenterConversationGetLatestConversationsDto> = [];
  for (const conversation of list) {
    const id = conversation.conversationId;
    if (id && !seen.has(id)) {
      seen.add(id);
      result.push(conversation);
    }
  }
  return result;
}

export function useInfiniteConversationList({
  folder,
  daysBack,
  isWebSocketEnabledAndReady,
  pollIntervalMs,
}: UseInfiniteConversationListOptions): UseInfiniteConversationListResult {
  const { httpClient, onUnauthorized } = useInboxConfig();
  const basePostData: Omit<SatelliteWebRenterConversationGetLatestConversationsRequestDto, 'beforeConversationId'> =
    useMemo(
      () => ({
        folder,
        daysBack: daysBack ?? undefined,
      }),
      [folder, daysBack],
    );

  // Query key carries an `'infinite'` discriminator so this useInfiniteQuery cache entry
  // doesn't collide with the generated single-page `useSatelliteWebRenterConversationHandler_getLatestConversations`
  // hook on the same `basePostData` — the two store incompatible shapes
  // (`InfiniteData<Response>` vs `Response`), so sharing a key would let one overwrite the
  // other's cache. Cross-cutting invalidation (HP-806B's WebSocket bus) flows through
  // `meta.tags: [CONVERSATION_LIST_QUERY_TAG]` rather than key matching.
  const queryKey = useMemo(
    () =>
      [
        'infinite',
        ...SatelliteWebRenterConversationHandler_getLatestConversations_queryOptions(basePostData).queryKey,
      ] as const,
    [basePostData],
  );

  const {
    data,
    error,
    refetch: infiniteRefetch,
    fetchNextPage: infiniteFetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery<
    SatelliteWebRenterConversationGetLatestConversationsResponseDto,
    Error,
    InfiniteData<SatelliteWebRenterConversationGetLatestConversationsResponseDto>,
    typeof queryKey,
    string | undefined
  >({
    // Inherit the generator's app-default options (staleTime, retries, error handler hooks,
    // etc. configured in `@zg-rentals/react-query5`'s `tsClientOptions.queryOptions`). The
    // generator's single-page hooks compose `queryOptions` the same way; we mirror the
    // contract here since `useInfiniteQuery` isn't emitted by the generator and needs a
    // hand-written wrapper.
    ...tsClientOptions.queryOptions,
    queryKey,
    queryFn: async ({ pageParam }) => {
      const postData: SatelliteWebRenterConversationGetLatestConversationsRequestDto = {
        ...basePostData,
        beforeConversationId: pageParam,
      };
      return SatelliteWebRenterConversationHandler_getLatestConversations(postData, httpClient);
    },
    meta: {
      tags: [CONVERSATION_LIST_QUERY_TAG],
      context: {
        api: 'satellite-api',
        details: 'getLatestConversations',
      },
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage?.hasNextPage !== true || !lastPage?.conversations?.length) return undefined;
      const last = lastPage.conversations[lastPage.conversations.length - 1];
      return last?.conversationId;
    },
    placeholderData: keepPreviousData,
    refetchInterval: isWebSocketEnabledAndReady ? undefined : pollIntervalMs,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 2000,
    gcTime: typeof window === 'undefined' ? Infinity : 5 * 60 * 1000,
  });

  useEffect(() => {
    if (error?.name === 'DatarouterError' && 'responseStatus' in error && error.responseStatus === 401) {
      onUnauthorized();
    }
  }, [error, onUnauthorized]);

  const conversations = useMemo(() => {
    if (!data?.pages) return [];
    const allConversations = data.pages.flatMap((page) => page?.conversations ?? []);
    return dedupeByConversationId(allConversations);
  }, [data?.pages]);

  const refetch = useCallback(async () => {
    await infiniteRefetch();
  }, [infiniteRefetch]);

  const fetchNextPage = useCallback(async () => {
    await infiniteFetchNextPage();
  }, [infiniteFetchNextPage]);

  return {
    conversations,
    hasNextPage: hasNextPage ?? false,
    isLoading,
    isFetchingNextPage,
    isRefetching: isFetching && !isFetchingNextPage && !isLoading,
    error: error as Error | null,
    refetch,
    fetchNextPage,
  };
}
